// ThreadedComment.jsx
import React, { useState } from 'react';
import { Reply } from 'lucide-react';

const ThreadedComment = ({ 
  comment, 
  onReply, 
  onReaction,
  currentUser,
  reactions = [],
  children
}) => {
  const [isReplying, setIsReplying] = useState(false);
  const [replyText, setReplyText] = useState('');

  // Helper function to calculate margin based on depth
  const getMarginClass = (depth) => {
    // Cap the maximum indentation at depth 4
    const maxDepth = Math.min(depth || 0, 4);
    // Use specific margin classes for each depth level
    switch (maxDepth) {
      case 1:
        return 'ml-8';
      case 2:
        return 'ml-12';
      case 3:
        return 'ml-16';
      case 4:
        return 'ml-20';
      default:
        return '';
    }
  };

  // Calculate visual indicator for depth
  const getDepthIndicator = (depth) => {
    if (!depth) return null;
    
    return (
      <div 
        className={`absolute left-0 top-0 bottom-0 border-l-2 border-gray-200
          ${depth > 1 ? 'border-dashed' : ''}`}
        style={{ 
          left: `${(depth - 1) * 1}rem`,
        }}
      />
    );
  };

  const handleSubmitReply = (e) => {
    if (e) {
      e.preventDefault();
    }
    if (replyText.trim()) {
      onReply(comment.id, replyText);
      setReplyText('');
      setIsReplying(false);
    }
  };

  const commonEmojis = ['👍', '❤️', '❓', '👎', '😕', '🆘'];

  return (
    <div className="flex flex-col gap-3 relative">
      <div className={`${getMarginClass(comment?.depth)} relative`}>
        {getDepthIndicator(comment?.depth)}
        <div className="flex gap-3 p-4 bg-gray-50 rounded-lg relative">
          <div className="flex-shrink-0">
            <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
              {comment?.user_name?.charAt(0).toUpperCase()}
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <p className="text-sm font-medium text-gray-900">
                {comment?.user_name || 'Anonymous'}
              </p>
              <span className="text-xs text-gray-500">
                {new Date(comment?.created_at).toLocaleString()}
              </span>
            </div>
            
            <p className="text-sm text-gray-700 whitespace-pre-wrap mb-2">
              {comment?.comment_text}
            </p>

            {/* Reactions */}
            <div className="flex flex-wrap gap-2 mt-2">
              {commonEmojis.map(emoji => {
                const commentReactions = reactions.filter(r => 
                  r.emoji === emoji && r.comment_id === comment?.id
                );
                const hasReacted = commentReactions.some(r => r.user_id === currentUser?.id);
                const count = commentReactions.length;
                
                return (
                  <button
                    key={emoji}
                    onClick={() => onReaction(comment.id, emoji)}
                    className={`px-2 py-1 rounded-full text-xs flex items-center gap-1 
                      ${hasReacted ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}
                      hover:bg-green-50 transition-colors duration-200`}
                  >
                    <span>{emoji}</span>
                    {count > 0 && <span>{count}</span>}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => setIsReplying(!isReplying)}
              className="mt-2 text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
            >
              <Reply className="w-4 h-4" />
              Reply
            </button>
          </div>
        </div>

        {/* Reply Form */}
        {isReplying && (
          <div className="mt-2">
            <textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmitReply();
                }
              }}
              placeholder="Write a reply... (Press Enter to submit, Shift+Enter for new line)"
              className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              rows={3}
            />
            <div className="flex justify-end gap-2 mt-2">
              <button
                type="button"
                onClick={() => setIsReplying(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {children}
    </div>
  );
};

export default ThreadedComment;